import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend, LineChart, Line } from "recharts";
import { appelsOffresService, servicesService, societesService, depotsService, analysesService } from '@/services';
import type { AoAccessReportEntry, AoAccessReportItem } from '@/services/appels-offres.service';
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X, TrendingUp, Users, FileText, CheckCircle, Clock, DollarSign, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
interface AppelOffre {
  id: string;
  titre: string;
  reference: string;
  service_responsable_id: string;
  ao_manager: string;
  statut: string;
  date_limite: string;
  created_at?: string;
  date_publication?: string;
}
interface Service {
  id: string;
  nom: string;
}
interface Societe {
  id: string;
  nom: string;
}
interface Depot {
  id: string;
  ao_id: string;
  candidat_id: string;
  candidats: {
    societe_id: string;
  };
}
interface Analysis {
  id: string;
  ao_id: string;
  decision: string;
  overall_score: number;
  cost_estimate: number;
  tokens_in: number;
  tokens_out: number;
  created_at: string;
}
interface StatData {
  name: string;
  count: number;
}

interface AccessReportRow {
  ao: AoAccessReportItem;
  access: AoAccessReportEntry;
}

const COLORS = ['hsl(var(--primary))', 'hsl(210 100% 80%)', 'hsl(var(--accent))', 'hsl(210 100% 85%)', 'hsl(210 100% 75%)'];

const getAccessLabel = (permissionType: AoAccessReportEntry["permissionType"]) => {
  if (permissionType === "owner") return "Propriétaire";
  if (permissionType === "admin") return "Admin";
  if (permissionType === "write") return "Modification";
  return "Lecture";
};

const getUserLabel = (access: AoAccessReportEntry) => {
  return access.user?.collaborateur?.nom || access.user?.email || "Utilisateur supprimé";
};

const getUserStatusLabel = (status?: string) => {
  if (status === "banned") return "Suspendu";
  if (status === "unverified") return "Non vérifié";
  return "Actif";
};

const Reporting = () => {
  const [selectedDimension, setSelectedDimension] = useState<string>("service");
  const [selectedAccessAoId, setSelectedAccessAoId] = useState<string>("all");
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [filteredAOs, setFilteredAOs] = useState<AppelOffre[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState<{ title: string; items: any[] }>({ title: "", items: [] });
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusDialogData, setStatusDialogData] = useState<{ title: string; items: AppelOffre[] }>({ title: "", items: [] });
  const [decisionDialogOpen, setDecisionDialogOpen] = useState(false);
  const [decisionDialogData, setDecisionDialogData] = useState<{ title: string; items: any[] }>({ title: "", items: [] });

  // Fetch appels d'offres
  const {
    data: appelsOffres,
    isLoading: isLoadingAO
  } = useQuery({
    queryKey: ["appels-offres-stats"],
    queryFn: async () => {
      const data = await appelsOffresService.getAll();
      return (data || []).map((ao: any) => ({
        id: ao.id,
        titre: ao.titre,
        reference: ao.reference,
        service_responsable_id: ao.serviceResponsableId,
        ao_manager: ao.aoManager,
        statut: ao.statut,
        date_limite: ao.dateLimite,
        created_at: ao.createdAt,
        date_publication: ao.datePublication
      })) as AppelOffre[];
    }
  });

  // Fetch services
  const {
    data: services,
    isLoading: isLoadingServices
  } = useQuery({
    queryKey: ["services-stats"],
    queryFn: async () => {
      const data = await servicesService.getAll();
      return data as Service[];
    }
  });

  // Fetch sociétés
  const {
    data: societes,
    isLoading: isLoadingSocietes
  } = useQuery({
    queryKey: ["societes-stats"],
    queryFn: async () => {
      const data = await societesService.getAll();
      return data as Societe[];
    }
  });

  // Fetch depots avec candidats
  const {
    data: depots,
    isLoading: isLoadingDepots
  } = useQuery({
    queryKey: ["depots-stats"],
    queryFn: async () => {
      const data = await depotsService.getAll();
      return (data || []).map((d: any) => ({
        id: d.id,
        ao_id: d.aoId,
        candidat_id: d.candidatId,
        candidats: d.candidat ? { societe_id: d.candidat.societeId } : null
      })) as Depot[];
    }
  });

  // Fetch analyses
  const {
    data: analyses,
    isLoading: isLoadingAnalyses
  } = useQuery({
    queryKey: ["analyses-stats"],
    queryFn: async () => {
      const data = await analysesService.getAll();
      return (data || []).map((a: any) => ({
        id: a.id,
        ao_id: a.aoId,
        decision: a.decision,
        overall_score: a.overallScore,
        cost_estimate: a.costEstimate,
        tokens_in: a.tokensIn,
        tokens_out: a.tokensOut,
        created_at: a.createdAt
      })) as Analysis[];
    }
  });

  const {
    data: accessReport,
    isLoading: isLoadingAccessReport
  } = useQuery({
    queryKey: ["ao-access-report"],
    queryFn: async () => appelsOffresService.getAccessReport()
  });

  const isLoading = isLoadingAO || isLoadingServices || isLoadingSocietes || isLoadingDepots || isLoadingAnalyses;

  // Process data based on selected dimension
  const getStatData = (): StatData[] => {
    if (!appelsOffres || !services) return [];
    if (selectedDimension === "service") {
      const serviceCounts: Record<string, number> = {};
      appelsOffres.forEach(ao => {
        if (ao.service_responsable_id) {
          const service = services.find(s => s.id === ao.service_responsable_id);
          const serviceName = service?.nom || "Service non défini";
          serviceCounts[serviceName] = (serviceCounts[serviceName] || 0) + 1;
        } else {
          serviceCounts["Service non défini"] = (serviceCounts["Service non défini"] || 0) + 1;
        }
      });
      return Object.entries(serviceCounts).map(([name, count]) => ({
        name,
        count
      }));
    }
    if (selectedDimension === "manager") {
      const managerCounts: Record<string, number> = {};
      appelsOffres.forEach(ao => {
        const manager = ao.ao_manager || "Manager non défini";
        managerCounts[manager] = (managerCounts[manager] || 0) + 1;
      });
      return Object.entries(managerCounts).map(([name, count]) => ({
        name,
        count
      }));
    }
    if (selectedDimension === "societe") {
      if (!depots || !societes) return [];
      const societeCounts: Record<string, number> = {};
      depots.forEach(depot => {
        if (depot.candidats?.societe_id) {
          const societe = societes.find(s => s.id === depot.candidats.societe_id);
          const societeName = societe?.nom || "Société non définie";
          societeCounts[societeName] = (societeCounts[societeName] || 0) + 1;
        } else {
          societeCounts["Société non définie"] = (societeCounts["Société non définie"] || 0) + 1;
        }
      });
      return Object.entries(societeCounts).map(([name, count]) => ({
        name,
        count
      }));
    }
    return [];
  };
  const statData = getStatData();

  // Reset filter when dimension changes
  useEffect(() => {
    setSelectedFilter(null);
    setFilteredAOs([]);
  }, [selectedDimension]);

  // Handle bar click to show dialog with detailed data
  const handleBarClick = (data: StatData) => {
    if (!appelsOffres || !services) return;
    let items: any[] = [];
    let title = "";
    
    if (selectedDimension === "service") {
      title = `AOs pour le service : ${data.name}`;
      if (data.name === "Service non défini") {
        items = appelsOffres.filter(ao => !ao.service_responsable_id);
      } else {
        const service = services.find(s => s.nom === data.name);
        if (service) {
          items = appelsOffres.filter(ao => ao.service_responsable_id === service.id);
        }
      }
    } else if (selectedDimension === "manager") {
      title = `AOs gérés par : ${data.name}`;
      if (data.name === "Manager non défini") {
        items = appelsOffres.filter(ao => !ao.ao_manager);
      } else {
        items = appelsOffres.filter(ao => ao.ao_manager === data.name);
      }
    } else if (selectedDimension === "societe") {
      title = `Dépôts de la société : ${data.name}`;
      if (!depots || !societes) return;
      
      if (data.name === "Société non définie") {
        items = depots.filter(d => !d.candidats?.societe_id).map(depot => {
          const ao = appelsOffres.find(a => a.id === depot.ao_id);
          return {
            id: depot.id,
            ao_titre: ao?.titre || "N/A",
            ao_reference: ao?.reference || "N/A",
            ao_manager: ao?.ao_manager || "N/A",
          };
        });
      } else {
        const societe = societes.find(s => s.nom === data.name);
        if (societe) {
          items = depots
            .filter(d => d.candidats?.societe_id === societe.id)
            .map(depot => {
              const ao = appelsOffres.find(a => a.id === depot.ao_id);
              return {
                id: depot.id,
                ao_titre: ao?.titre || "N/A",
                ao_reference: ao?.reference || "N/A",
                ao_manager: ao?.ao_manager || "N/A",
              };
            });
        }
      }
    }
    
    setDialogData({ title, items });
    setDialogOpen(true);
  };

  // Clear filter
  const clearFilter = () => {
    setSelectedFilter(null);
    setFilteredAOs([]);
  };

  // Calculate global performance metrics
  const getGlobalMetrics = () => {
    const totalAOs = appelsOffres?.length || 0;
    const totalDepots = depots?.length || 0;
    const totalAnalyses = analyses?.length || 0;
    const aoWithDepots = new Set(depots?.map(d => d.ao_id) || []).size;
    const responseRate = totalAOs > 0 ? aoWithDepots / totalAOs * 100 : 0;
    const shortlistCount = analyses?.filter(a => a.decision === 'shortlist').length || 0;
    const considerCount = analyses?.filter(a => a.decision === 'consider').length || 0;
    const rejectCount = analyses?.filter(a => a.decision === 'reject').length || 0;
    const avgScore = analyses && analyses.length > 0 ? analyses.reduce((sum, a) => sum + (a.overall_score || 0), 0) / analyses.length : 0;
    const totalCost = analyses?.reduce((sum, a) => sum + (a.cost_estimate || 0), 0) || 0;
    return {
      totalAOs,
      totalDepots,
      totalAnalyses,
      responseRate: responseRate.toFixed(1),
      shortlistCount,
      considerCount,
      rejectCount,
      avgScore: avgScore.toFixed(1),
      totalCost: totalCost.toFixed(2)
    };
  };
  const metrics = getGlobalMetrics();

  const filteredAccessReport = (accessReport || []).filter((item) => (
    selectedAccessAoId === "all" || item.aoId === selectedAccessAoId
  ));
  const accessRows: AccessReportRow[] = filteredAccessReport.flatMap((ao) => (
    ao.accesses.map((access) => ({ ao, access }))
  ));
  const accessTotals = filteredAccessReport.reduce(
    (totals, ao) => ({
      read: totals.read + ao.readCount,
      write: totals.write + ao.writeCount,
      total: totals.total + ao.totalAccessCount,
    }),
    { read: 0, write: 0, total: 0 }
  );

  // Get status distribution
  const getStatusData = () => {
    if (!appelsOffres) return [];
    const statusCounts: Record<string, number> = {};
    appelsOffres.forEach(ao => {
      const status = ao.statut || "Non défini";
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });
    return Object.entries(statusCounts).map(([name, value]) => ({
      name,
      value
    }));
  };

  // Handle status pie chart click
  const handleStatusClick = (data: any) => {
    if (!appelsOffres) return;
    const statusName = data.name;
    const filteredByStatus = appelsOffres.filter(ao => 
      (ao.statut || "Non défini") === statusName
    );
    setStatusDialogData({
      title: `AOs avec le statut : ${statusName}`,
      items: filteredByStatus
    });
    setStatusDialogOpen(true);
  };

  // Get decision distribution
  const getDecisionData = () => {
    if (!analyses) return [];
    return [{
      name: 'Shortlist',
      value: metrics.shortlistCount,
      fill: 'hsl(var(--primary))'
    }, {
      name: 'Consider',
      value: metrics.considerCount,
      fill: 'hsl(var(--accent))'
    }, {
      name: 'Reject',
      value: metrics.rejectCount,
      fill: 'hsl(var(--destructive))'
    }].filter(item => item.value > 0);
  };

  // Handle decision pie chart click
  const handleDecisionClick = (data: any) => {
    if (!analyses || !appelsOffres) return;
    const decisionName = data.name.toLowerCase();
    const filteredAnalyses = analyses.filter(a => a.decision === decisionName);
    
    // Enrichir les données avec les informations AO
    const enrichedItems = filteredAnalyses.map(analysis => {
      const ao = appelsOffres.find(a => a.id === analysis.ao_id);
      return {
        id: analysis.id,
        ao_titre: ao?.titre || "N/A",
        ao_reference: ao?.reference || "N/A",
        overall_score: analysis.overall_score,
        decision: analysis.decision,
        created_at: analysis.created_at
      };
    });

    setDecisionDialogData({
      title: `Analyses avec la décision : ${data.name}`,
      items: enrichedItems
    });
    setDecisionDialogOpen(true);
  };

  // Get timeline data (AOs créés par mois)
  const getTimelineData = () => {
    if (!appelsOffres) return [];
    const monthCounts: Record<string, number> = {};
    appelsOffres.forEach(ao => {
      const date = new Date(ao.created_at || ao.date_publication || Date.now());
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthCounts[monthKey] = (monthCounts[monthKey] || 0) + 1;
    });
    return Object.entries(monthCounts).sort(([a], [b]) => a.localeCompare(b)).map(([month, count]) => ({
      month,
      count
    }));
  };
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-card-foreground">{`${label}`}</p>
          <p className="text-primary">
            {selectedDimension === "societe" ? `Nombre de réponses : ${payload[0].value}` : `Nombre d'AO : ${payload[0].value}`}
          </p>
        </div>;
    }
    return null;
  };
  return <Layout>
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-foreground">Rapport de Performance Global</h1>
        </div>

        {/* KPI Cards */}
        {isLoading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-32" />)}
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Total AOs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{metrics.totalAOs}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Total Dépôts
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{metrics.totalDepots}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Taux de Réponse
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{metrics.responseRate}%</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Analyses
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{metrics.totalAnalyses}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Score Moyen
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{metrics.avgScore}/100</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Shortlist</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-primary">{metrics.shortlistCount}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Consider</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-accent">{metrics.considerCount}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Coût Total
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">CHF {metrics.totalCost}</div>
              </CardContent>
            </Card>
          </div>}

        <Card>
          <CardHeader>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5" />
                  Accès par AO
                </CardTitle>
                <CardDescription>Lecture et modification par utilisateur</CardDescription>
              </div>
              <Select value={selectedAccessAoId} onValueChange={setSelectedAccessAoId}>
                <SelectTrigger className="w-full lg:w-[420px]">
                  <SelectValue placeholder="Filtrer par AO" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les AO</SelectItem>
                  {(accessReport || []).map((ao) => (
                    <SelectItem key={ao.aoId} value={ao.aoId}>
                      {ao.reference} - {ao.titre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoadingAccessReport ? (
              <div className="space-y-3">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-48 w-full" />
              </div>
            ) : accessRows.length > 0 ? (
              <>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <div className="rounded-md border p-3">
                    <div className="text-sm text-muted-foreground">AO filtrées</div>
                    <div className="text-2xl font-semibold">{filteredAccessReport.length}</div>
                  </div>
                  <div className="rounded-md border p-3">
                    <div className="text-sm text-muted-foreground">Accès lecture</div>
                    <div className="text-2xl font-semibold">{accessTotals.read}</div>
                  </div>
                  <div className="rounded-md border p-3">
                    <div className="text-sm text-muted-foreground">Accès modification</div>
                    <div className="text-2xl font-semibold">{accessTotals.write}</div>
                  </div>
                </div>

                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>AO</TableHead>
                        <TableHead>Utilisateur</TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead>Accès</TableHead>
                        <TableHead>Compte</TableHead>
                        <TableHead>Accordé par</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {accessRows.map(({ ao, access }) => (
                        <TableRow key={`${ao.aoId}-${access.permissionId || access.user?.id || access.permissionType}`}>
                          <TableCell>
                            <div className="font-medium">{ao.reference}</div>
                            <div className="max-w-[320px] truncate text-sm text-muted-foreground">{ao.titre}</div>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{getUserLabel(access)}</div>
                            <div className="text-sm text-muted-foreground">{access.user?.email || "N/A"}</div>
                          </TableCell>
                          <TableCell>{access.user?.collaborateur?.service?.nom || "N/A"}</TableCell>
                          <TableCell>
                            <Badge variant={access.accessMode === "write" ? "default" : "secondary"}>
                              {getAccessLabel(access.permissionType)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                access.user?.userStatus === "banned"
                                  ? "destructive"
                                  : access.user?.userStatus === "unverified"
                                    ? "secondary"
                                    : "outline"
                              }
                            >
                              {getUserStatusLabel(access.user?.userStatus)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {access.source === "owner" ? "Propriété AO" : access.grantedBy?.email || "N/A"}
                          </TableCell>
                          <TableCell>
                            {access.grantedAt ? new Date(access.grantedAt).toLocaleDateString() : "N/A"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center rounded-md border py-12 text-muted-foreground">
                Aucun accès à afficher
              </div>
            )}
          </CardContent>
        </Card>

        {/* Charts Row 1: Status & Decisions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Répartition par Statut</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? <Skeleton className="h-[300px]" /> : <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie 
                        data={getStatusData()} 
                        cx="50%" 
                        cy="50%" 
                        labelLine={false} 
                        label={({name, percent}) => `${name} (${(percent * 100).toFixed(0)}%)`} 
                        outerRadius={80} 
                        fill="hsl(var(--primary))" 
                        dataKey="value"
                        onClick={handleStatusClick}
                        style={{ cursor: 'pointer' }}
                      >
                        {getStatusData().map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Répartition des Décisions</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? <Skeleton className="h-[300px]" /> : <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie 
                        data={getDecisionData()} 
                        cx="50%" 
                        cy="50%" 
                        labelLine={false} 
                        label={({name, percent}) => `${name} (${(percent * 100).toFixed(0)}%)`} 
                        outerRadius={80} 
                        dataKey="value"
                        onClick={handleDecisionClick}
                        style={{ cursor: 'pointer' }}
                      >
                        {getDecisionData().map((entry, index) => <Cell key={`cell-${index}`} fill={entry.fill} />)}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>}
            </CardContent>
          </Card>
        </div>


        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Réponses (dossiers / CV) par sociétés</CardTitle>
              <Select value={selectedDimension} onValueChange={setSelectedDimension}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Choisir une dimension" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="service">Service responsable</SelectItem>
                  <SelectItem value="manager">AO Manager</SelectItem>
                  <SelectItem value="societe">Réponses par Société</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? <div className="space-y-4">
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-64 w-full" />
              </div> : statData.length > 0 ? <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={statData} margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 60
              }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" angle={-45} textAnchor="end" height={80} interval={0} />
                    <YAxis stroke="hsl(var(--muted-foreground))" />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]} onClick={handleBarClick} style={{
                  cursor: 'pointer'
                }}>
                      {statData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div> : <div className="flex items-center justify-center h-64 text-muted-foreground">
                Aucune donnée disponible
              </div>}
          </CardContent>
        </Card>

        {/* Submissions by Company Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Nombre de soumissions par société</CardTitle>
            <CardDescription>Nombre total de dossiers déposés par chaque société</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-[400px]" /> : (() => {
              const submissionsBySociete: Record<string, number> = {};
              depots?.forEach(depot => {
                if (depot.candidats?.societe_id) {
                  const societe = societes?.find(s => s.id === depot.candidats.societe_id);
                  const societeName = societe?.nom || "Société non définie";
                  submissionsBySociete[societeName] = (submissionsBySociete[societeName] || 0) + 1;
                }
              });
              const submissionsData = Object.entries(submissionsBySociete)
                .map(([name, count]) => ({ name, count }))
                .sort((a, b) => b.count - a.count);
              
              return submissionsData.length > 0 ? (
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={submissionsData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="name" 
                        stroke="hsl(var(--muted-foreground))" 
                        angle={-45} 
                        textAnchor="end" 
                        height={100}
                        interval={0}
                      />
                      <YAxis stroke="hsl(var(--muted-foreground))" />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]} fill="hsl(var(--primary))">
                        {submissionsData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  Aucune soumission trouvée
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Dialog for detailed data */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{dialogData.title}</DialogTitle>
              <DialogDescription>
                {dialogData.items.length} élément{dialogData.items.length > 1 ? 's' : ''} trouvé{dialogData.items.length > 1 ? 's' : ''}
              </DialogDescription>
            </DialogHeader>
            {dialogData.items.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    {selectedDimension === "societe" ? (
                      <>
                        <TableHead>Titre AO</TableHead>
                        <TableHead>Référence</TableHead>
                        <TableHead>Manager</TableHead>
                      </>
                    ) : (
                      <>
                        <TableHead>Titre</TableHead>
                        <TableHead>Référence</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Date limite</TableHead>
                      </>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dialogData.items.map((item, index) => (
                    <TableRow key={item.id || index}>
                      {selectedDimension === "societe" ? (
                        <>
                          <TableCell className="font-medium">{item.ao_titre}</TableCell>
                          <TableCell>{item.ao_reference}</TableCell>
                          <TableCell>{item.ao_manager}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="font-medium">{item.titre}</TableCell>
                          <TableCell>{item.reference}</TableCell>
                          <TableCell>
                            <Badge 
                              variant={
                                item.statut === "terminé" ? "destructive" : 
                                item.statut === "publié" ? "default" : 
                                "secondary"
                              }
                              className={item.statut === "en cours" ? "bg-yellow-500 text-black hover:bg-yellow-600" : ""}
                            >
                              {item.statut}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {item.date_limite ? new Date(item.date_limite).toLocaleDateString() : "N/A"}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                Aucune donnée disponible
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Dialog for status chart */}
        <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{statusDialogData.title}</DialogTitle>
              <DialogDescription>
                {statusDialogData.items.length} élément{statusDialogData.items.length > 1 ? 's' : ''} trouvé{statusDialogData.items.length > 1 ? 's' : ''}
              </DialogDescription>
            </DialogHeader>
            {statusDialogData.items.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Titre</TableHead>
                    <TableHead>Référence</TableHead>
                    <TableHead>Manager</TableHead>
                    <TableHead>Date limite</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statusDialogData.items.map((ao) => (
                    <TableRow key={ao.id}>
                      <TableCell className="font-medium">{ao.titre}</TableCell>
                      <TableCell>{ao.reference}</TableCell>
                      <TableCell>{ao.ao_manager || "N/A"}</TableCell>
                      <TableCell>
                        {ao.date_limite ? new Date(ao.date_limite).toLocaleDateString() : "N/A"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                Aucune donnée disponible
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Dialog for decision chart */}
        <Dialog open={decisionDialogOpen} onOpenChange={setDecisionDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{decisionDialogData.title}</DialogTitle>
              <DialogDescription>
                {decisionDialogData.items.length} analyse{decisionDialogData.items.length > 1 ? 's' : ''} trouvée{decisionDialogData.items.length > 1 ? 's' : ''}
              </DialogDescription>
            </DialogHeader>
            {decisionDialogData.items.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Titre AO</TableHead>
                    <TableHead>Référence</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Décision</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {decisionDialogData.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.ao_titre}</TableCell>
                      <TableCell>{item.ao_reference}</TableCell>
                      <TableCell>{item.overall_score}/100</TableCell>
                      <TableCell>
                        <Badge 
                          variant={
                            item.decision === "shortlist" ? "default" : 
                            item.decision === "consider" ? "secondary" : 
                            "destructive"
                          }
                        >
                          {item.decision}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {item.created_at ? new Date(item.created_at).toLocaleDateString() : "N/A"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                Aucune donnée disponible
              </div>
            )}
          </DialogContent>
        </Dialog>

      </div>
    </Layout>;
};
export default Reporting;
